/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo } from 'react';

import useTimezone from 'lib/hooks/useTimezone';
import useRetainedValue from 'lib/hooks/useRetainedValue';
import { cn, ia, iaRa, inRn, io } from 'lib/helpers/utility';
import { openInNewTab, wantsNewTab } from 'lib/helpers/openInNewTab';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useIsShellHosted } from 'lib/context/ShellContext/ShellContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';

import AGTable from '../AGTable/AGTable';
import { hasActiveFilters } from '../Filters/lib/hasActiveFilters';
import { useTableStatusBar } from '../AGTable/lib/useTableStatusBar';

import TableHeader from './components/TableHeader';
import useRowHighlight from './lib/useRowHighlight';
import TableFilters from './components/TableFilters';
import useOpenHighlighted from './lib/useOpenHighlighted';
import HighlightBanner from './components/HighlightBanner';
import { createQueryFilters } from './lib/createQueryFilters';
import ChromeActionsGate from './components/ChromeActionsGate';
import { useTableAutoHeight } from './hooks/useTableAutoHeight';
import { RowSelectionActions } from './components/RowSelectionActions';
import {
  DEFAULT_COLUMN_DEFS,
  DEFAULT_PAGE_COUNT,
  DEFAULT_STALE_TIME,
  GRID_OPTIONS
} from './lib/constants';

const MODAL_CLOSE_MS = 300;

const AgTablePage = (props) => {
  const {
    defaultFilters = {},
    defaultColumns,
    name,
    pagination = true,
    getExportingData,
    exportParams,
    syncFiltersToUrl,
    ...rest
  } = props;

  return (
    <TableContextProvider
      defaultFilters={defaultFilters}
      cols={defaultColumns}
      name={name}
      pagination={pagination}
      getData={getExportingData}
      exportParams={exportParams}
      syncFiltersToUrl={syncFiltersToUrl}
      {...rest}>
      <AgTablePageContent {...rest} />
    </TableContextProvider>
  );
};

const AgTablePageContent = ({
  useData,
  queryParams = {},
  queryOptions = {},
  ModalComponent,
  onNewClick,
  subtitle,
  onCellClicked,
  onSelectRow,
  rowHref,
  // The filters shown as quick chips, in order (or a function of the active view); every other
  // filter waits in More filters. Without it the row picks up to three on its own.
  quickFilters,
  headerActions,
  refresh = false,
  skeletonSize,
  noTitle,
  pageTitle,
  compactHeader,
  showExport,
  exportInFilterRow = false,
  switchItems,
  headerViews,
  banner,
  title,
  rightHeaderActions,
  noNewButton,
  newButtonProps,
  actions,
  noHeader,
  formatData,
  queryKey,
  newButtonText,
  newButtonDisabledReason,
  disabledCells = [],
  type,
  dependencies,
  noDisplayButton = false,
  noFilterActions = false,
  alwaysShowFilters = false,
  hideStatusBar = false,
  alwaysShowStatusBar = false,
  CustomPagination,
  CustomCount,
  fetchOnlyEnabledColumns = false,
  selectedRowIds,
  extraColumns = [],
  className,
  disableActions,
  practiceId,
  ...rest
}) => {
  const isShellHosted = useIsShellHosted();
  const {
    name,
    limit,
    pagination,
    filters,
    changeFilters,
    updateFilters,
    defaultFilters,
    initialFilters,
    errors,
    forceRefresh,
    isEditingFilter,
    selectedItem,
    setSelectedItem,
    showModal,
    setShowModal,
    filtersInitialized,
    sortField,
    sort,
    view,
    isSwitchItemView,
    page,
    colDefs,
    columnsReady,
    gridApi,
    gridWrapperRef
  } = useTableContext();

  const timezone = useTimezone();

  const enabledColumns = useMemo(() => {
    if (!fetchOnlyEnabledColumns || !ia(colDefs)) return;
    const columns = colDefs.filter((col) => !col.hide && col.field).map((col) => col.field);
    return [...columns, ...extraColumns];
  }, [colDefs, fetchOnlyEnabledColumns]);

  const queryFilters = useMemo(() => {
    if (!filtersInitialized) return {};
    // On the render that switches view, filters still hold the previous view's values until
    // useReactFilters drops them; only the ones this view offers may reach the request.
    const offeredFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(([key]) => key in (defaultFilters || {}))
    );
    const result = createQueryFilters(offeredFilters, isSwitchItemView && view, timezone);
    return result;
  }, [filters, defaultFilters, view, isSwitchItemView, filtersInitialized, timezone]);

  const queryFiltersKey = useMemo(() => JSON.stringify(queryFilters), [queryFilters]);

  const newDependencies = useMemo(() => {
    const baseDeps = [
      limit,
      page,
      sort,
      queryFiltersKey,
      view,
      sortField,
      ...(fetchOnlyEnabledColumns && ia(enabledColumns) ? [enabledColumns.join(',')] : [])
    ];
    if (ia(dependencies)) {
      return [...baseDeps, ...dependencies];
    }
    return [...baseDeps, ...Object.values(queryParams)];
  }, [
    limit,
    page,
    sort,
    queryFiltersKey,
    queryParams,
    view,
    dependencies,
    sortField,
    enabledColumns
  ]);

  const baseParams = useMemo(
    () => ({
      ...(sortField && { sort: sortField }),
      filters: queryFilters,
      ...(fetchOnlyEnabledColumns && { enabledColumns }),
      ...queryParams,
      ...(isSwitchItemView && view != null && { view })
    }),
    [
      sortField,
      queryFilters,
      fetchOnlyEnabledColumns,
      enabledColumns,
      queryParams,
      isSwitchItemView,
      view
    ]
  );

  const { data, refetch, isLoading, isRefetching } = useData({
    name,
    params: { limit, page, withCount: true, ...baseParams },
    options: {
      staleTime: DEFAULT_STALE_TIME,
      enabled: filtersInitialized && (!fetchOnlyEnabledColumns || columnsReady),
      ...queryOptions
    },
    dependencies: newDependencies
  });

  const exportQuery = useData({
    name,
    params: { page: 1, ...baseParams, exporting: true, pagination: false },
    options: {
      ...queryOptions,
      enabled: false,
      cacheTime: 0,
      staleTime: 0,
      keepPreviousData: false,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // The table never renders from this query — it exists only so an export can refetch
      // it on demand, so it must not re-render the page or run the caller's side effects.
      notifyOnChangeProps: [],
      onSuccess: undefined,
      onError: undefined,
      onSettled: undefined
    },
    dependencies: [...newDependencies, 'export']
  });

  const loading = !filtersInitialized || isLoading;
  const transitioning = isRefetching && !isLoading;

  const selectRows = useCallback(
    (response) => {
      if (queryKey === 'direct.format' && formatData)
        return formatData(response, { filters: queryFilters });

      return formatData
        ? formatData(iaRa(response), { filters: queryFilters })
        : iaRa(response?.[queryKey || name]);
    },
    [formatData, queryFilters, queryKey, name]
  );

  const items = useMemo(() => {
    if (loading) return;
    return selectRows(data);
  }, [data, loading, selectRows]);

  const { highlight, rowClassRules, isMissing } = useRowHighlight(rest.rowClassRules, items);
  const openHighlighted = useCallback(
    (row) => {
      setSelectedItem(row);
      setShowModal(true);
    },
    [setSelectedItem, setShowModal]
  );

  useOpenHighlighted({
    highlight,
    items,
    isMissing,
    isLoading: loading || transitioning,
    canOpen: !!ModalComponent,
    open: openHighlighted
  });

  const exportRefetch = exportQuery.refetch;

  const fetchAllPagesData = useCallback(async () => {
    const { data: exportData, error } = await exportRefetch();

    // The backend answers 200 on business errors and the query itself can fail outright.
    // Without these checks either one reduces to an empty array, which the export reports
    // as "No data to export" — hiding a timeout or a permission error behind a wrong message.
    if (error) throw error;

    if (exportData?.code !== undefined && exportData.code !== 0) {
      throw new Error(exportData.error || 'Export data could not be loaded');
    }

    return iaRa(selectRows(exportData));
  }, [exportRefetch, selectRows]);

  const count = inRn(data?.count) ?? DEFAULT_PAGE_COUNT;
  const statusPanelData = useMemo(() => data?.statusPanelData, [data]);

  const { isAutoHeight } = useTableAutoHeight({ count });

  const serializedSelectedRowIds = useMemo(
    () => JSON.stringify(selectedRowIds || []),
    [selectedRowIds]
  );

  useEffect(() => {
    if (!ia(selectedRowIds) || !gridApi || loading) return;

    gridApi.deselectAll();

    const ids = selectedRowIds.filter((id) => id != null && id !== '');
    if (!ia(ids)) return;

    const idSet = new Set(ids.map(String));

    gridApi.forEachNode((node) => {
      const rowId = node.data?.id ?? node.data?.value;
      if (rowId != null && idSet.has(String(rowId))) {
        node.setSelected(true, false);
      }
    });
  }, [gridApi, loading, serializedSelectedRowIds]);

  // A middle click never reaches onCellClicked.
  const onCellMiddleClick = (event) => {
    const href = event?.event?.button === 1 && !event?.node?.group && rowHref?.(event?.data, event);
    if (!href) return;
    event.event.preventDefault();
    openInNewTab(href);
  };

  const onCellClickedHandler = (event) => {
    if (loading || transitioning) return;

    if (['actions', '0', ...disabledCells].includes(event?.column?.colId) || event?.node?.group)
      return;

    const href = rowHref?.(event?.data, event);
    if (href && wantsNewTab(event?.event)) {
      openInNewTab(href);
      return;
    }

    if (typeof onCellClicked === 'function') {
      onCellClicked(event);
      return;
    }

    if (typeof onSelectRow === 'function') {
      onSelectRow(event?.data, event);
      return;
    }
    setSelectedItem(event?.data);
    setShowModal(true);
  };

  const onModalToggle = () => {
    setShowModal((prev) => !prev);
    setSelectedItem(null);
  };

  const retainedSelectedItem = useRetainedValue(selectedItem, {
    releaseAfterMs: MODAL_CLOSE_MS
  });

  const statusBar = useTableStatusBar({
    count,
    pagination,
    statusPanelData,
    hidden: hideStatusBar,
    alwaysShow: alwaysShowStatusBar,
    CustomCount,
    CustomPagination,
    data
  });

  const isPageTable = isShellHosted && !!pageTitle;
  // A table without a header never had Export; it gets one only when it asks for it.
  const exportInFilters = exportInFilterRow || (isShellHosted && io(defaultFilters) && !noHeader);
  const headerOnlyForChrome = noHeader && !isPageTable;

  const header = (
    <TableHeader
      category={name}
      onModalToggle={onNewClick || onModalToggle}
      hasNew={!!(onNewClick || ModalComponent) && !noNewButton}
      subtitle={subtitle}
      actions={headerActions}
      refresh={refresh}
      onRefresh={refetch}
      noTitle={isPageTable ? false : noTitle}
      compact={compactHeader}
      showExport={exportInFilters || noHeader ? false : showExport}
      switchItems={switchItems}
      views={headerViews}
      title={isPageTable ? pageTitle : title}
      count={count}
      rightActions={rightHeaderActions}
      loading={loading}
      newButtonText={newButtonText}
      newButtonProps={newButtonProps}
      newButtonDisabledReason={newButtonDisabledReason}
      statusPanelData={statusPanelData}
      data={data}
      hasData={ia(items)}
      getExportData={fetchAllPagesData}
      type={type}
    />
  );

  return (
    <div ref={gridWrapperRef} className={cn('flex h-full min-h-0 flex-col', className)}>
      {headerOnlyForChrome
        ? isShellHosted && <ChromeActionsGate>{header}</ChromeActionsGate>
        : header}
      <TableFilters
        filters={filters}
        initialFilters={initialFilters}
        defaultFilters={defaultFilters}
        changeFilters={changeFilters}
        updateFilters={updateFilters}
        category={name}
        isEditingFilter={isEditingFilter}
        items={switchItems}
        type={type}
        noDisplayButton={noDisplayButton}
        noFilterActions={noFilterActions}
        alwaysShowFilters={alwaysShowFilters || isShellHosted}
        quickFilters={quickFilters}
        hasData={ia(items) || hasActiveFilters(filters) || !!filters?.search?.searchTerm}
        itemsCount={items?.length}
        disableActions={disableActions}
        showExport={exportInFilters && showExport !== false}
        exportStatusPanelData={statusPanelData}
        exportData={data}
        getExportData={fetchAllPagesData}
      />
      {highlight && (
        <HighlightBanner
          highlight={highlight}
          isMissing={isMissing}
          hasMorePages={count > iaRa(items).length}
        />
      )}
      {banner}
      <div
        className={cn(
          'relative flex flex-col overflow-hidden',
          isAutoHeight ? 'h-auto' : 'h-full flex-1'
        )}>
        <div
          className={cn(
            'ag-theme-quartz transition-opacity duration-200',
            isAutoHeight ? 'h-auto' : 'h-full flex-1',
            transitioning && 'pointer-events-none opacity-50'
          )}>
          <AGTable
            key={forceRefresh}
            data={items}
            gridOptions={GRID_OPTIONS}
            defaultColDef={DEFAULT_COLUMN_DEFS}
            onCellClicked={onCellClickedHandler}
            onCellMouseDown={rowHref ? onCellMiddleClick : undefined}
            statusBar={statusBar}
            loading={loading}
            skeletonSize={skeletonSize}
            count={count}
            {...rest}
            rowClassRules={rowClassRules}
          />
        </div>
        <RowSelectionActions actions={actions} loading={loading} />
      </div>
      {ModalComponent && (
        <ModalComponent
          item={retainedSelectedItem}
          isOpen={!!showModal}
          handleClose={onModalToggle}
          filters={filters}
          errors={errors}
          category={name}
          tableData={items}
          practiceId={practiceId}
        />
      )}
    </div>
  );
};

export default AgTablePage;
